import { useLazyQuery, useMutation } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { FabiBackgroundJobStatus } from '@xing-com/crate-common-graphql-types';
import type { FabiJobSearchLoggedOutQueryGenerationRetrieveResult } from '@xing-com/crate-common-graphql-types';
import {
  CreateBackgroundJobIdDocument,
  GetLoggedOutQueryPollResultDocument,
  GetLoggedOutQueryPromptDocument,
} from '@xing-com/crate-jobs-graphql';

import { POLL_INTERVAL_MS, TIMEOUT_MS } from './constants';

// Depending on the error message from BE, we can also set the prompt error here.
// For now, we treat all errors as system errors. If prompt error (TBD) then set ErrorType.Prompt;
export enum ErrorType {
  System = 'SYSTEM',
  Prompt = 'PROMPT',
}

type FabiAIGenerationResult =
  FabiJobSearchLoggedOutQueryGenerationRetrieveResult | null;

export type UseFabiAIGenerationResult = [
  triggerFabiAIGeneration: (role: string, company: string) => Promise<void>,
  {
    data: FabiAIGenerationResult;
    loading: boolean;
    errorType: ErrorType | null;
  },
];

export const useFabiAIGeneration = ({
  pollInterval = POLL_INTERVAL_MS,
  timeoutMs = TIMEOUT_MS,
}: {
  pollInterval?: number;
  timeoutMs?: number;
} = {}): UseFabiAIGenerationResult => {
  const [createBackgroundJobId] = useMutation(CreateBackgroundJobIdDocument);
  const [getLoggedOutQueryPollResult, { data: pollData, error, stopPolling }] =
    useLazyQuery(GetLoggedOutQueryPollResultDocument);
  const [getLoggedOutQueryPrompt] = useLazyQuery(
    GetLoggedOutQueryPromptDocument
  );
  const [data, setData] = useState<FabiAIGenerationResult>(null);
  const [loading, setLoading] = useState(false);
  const [errorType, setErrorType] = useState<ErrorType | null>(null);
  const [backgroundJobId, setBackgroundJobId] = useState<string | null>(null);
  const timeoutRef = useRef<number | undefined>(undefined);

  const clearTimeoutRef = useCallback((): void => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const callGetResultError = useCallback((errorMessage?: string): void => {
    setErrorType(ErrorType.System);
    setLoading(false);
    console.error(
      errorMessage || 'An unknown error occurred while getting the prompt.'
    );
  }, []);

  const callLoggedOutQueryPrompt = useCallback(
    async (backgroundJobId: string): Promise<void> => {
      try {
        const { data: queryPromptData, error } = await getLoggedOutQueryPrompt({
          variables: { backgroundJobId },
        });

        if (error) {
          throw new Error(error.message);
        }

        const queryPromptResult =
          queryPromptData?.fabiJobSearchLoggedOutQueryGenerationRetrieve
            ?.__typename ===
            'FabiJobSearchLoggedOutQueryGenerationRetrieveResult' &&
          queryPromptData.fabiJobSearchLoggedOutQueryGenerationRetrieve;

        if (queryPromptResult) {
          setData(queryPromptResult);
          setLoading(false);
        } else {
          throw new Error('No data received from prompt generation.');
        }
      } catch (error) {
        callGetResultError(error.message);
      }
    },
    [callGetResultError, getLoggedOutQueryPrompt]
  );

  useEffect(() => {
    if (
      backgroundJobId &&
      (pollData?.fabiJobSearchLoggedOutQueryGenerationPoll ||
        pollData?.fabiJobSearchLoggedOutQueryGenerationPoll === null ||
        error)
    ) {
      try {
        const pollResult = pollData?.fabiJobSearchLoggedOutQueryGenerationPoll;

        if (
          error ||
          !pollResult ||
          pollResult.status === FabiBackgroundJobStatus.Error
        ) {
          throw new Error(error?.message || 'Error while polling for prompt.');
        }

        if (!timeoutRef.current) {
          timeoutRef.current = window.setTimeout(() => {
            stopPolling();
            callGetResultError('Timeout exceeded while polling for prompt.');
          }, timeoutMs);
        }

        if (pollResult.status === FabiBackgroundJobStatus.Ok) {
          clearTimeoutRef();
          stopPolling();
          callLoggedOutQueryPrompt(backgroundJobId);
        }
      } catch (error) {
        callGetResultError(error.message);
      }
    }

    return () => clearTimeoutRef();
  }, [
    backgroundJobId,
    callGetResultError,
    callLoggedOutQueryPrompt,
    clearTimeoutRef,
    error,
    pollData?.fabiJobSearchLoggedOutQueryGenerationPoll,
    stopPolling,
    timeoutMs,
  ]);

  const triggerFabiAIGeneration = useCallback(
    async (role: string, company: string): Promise<void> => {
      setLoading(true);
      clearTimeoutRef();

      try {
        const { data, errors } = await createBackgroundJobId({
          variables: {
            input: {
              userInput: { companyAndLocation: company, whatDoYouDo: role },
            },
          },
          fetchPolicy: 'network-only',
        });

        const fabiBackgroundJobId =
          data?.fabiJobSearchLoggedOutQueryGenerationSubmit?.__typename ===
            'FabiJobSearchLoggedOutQueryGenerationSubmitResult' &&
          data.fabiJobSearchLoggedOutQueryGenerationSubmit.backgroundJobId;

        if (!fabiBackgroundJobId || errors) {
          throw new Error('Error while creating background job ID.');
        }

        setBackgroundJobId(fabiBackgroundJobId);

        await getLoggedOutQueryPollResult({
          fetchPolicy: 'network-only',
          pollInterval,
          variables: {
            backgroundJobId: fabiBackgroundJobId,
          },
        });
      } catch (error) {
        callGetResultError(error.message);
      }
    },
    [
      callGetResultError,
      clearTimeoutRef,
      createBackgroundJobId,
      getLoggedOutQueryPollResult,
      pollInterval,
    ]
  );

  return [triggerFabiAIGeneration, { data, errorType, loading }];
};
